import React, { useState, useMemo } from 'react';
import Page from '../components/Page';
import { useActiveShift, useShiftsList, openShift, closeShift } from '../controllers/financial.controller';
import { useStoreSettings } from '../controllers/settings.controller';
import { 
  IconEye, 
  IconPrinter, 
  IconCash, 
  IconCheck, 
  IconX, 
  IconAlertTriangle,
  IconCoins,
  IconClock,
  IconUser,
  IconRefresh,
  IconArrowUpRight,
  IconArrowDownRight,
  IconListCheck
} from '@tabler/icons-react';
import { toast } from 'react-hot-toast';

export default function FinancialShiftPage() {
  const [activeTab, setActiveTab] = useState('active'); // 'active' | 'closed' | 'reports'
  const [txFilter, setTxFilter] = useState('all'); // 'all' | 'sales' | 'cash' | 'qr' | 'card' | 'due' | 'expenses' | 'discount' | 'complimentary' | 'refunds'
  const [selectedTx, setSelectedTx] = useState(new Set());

  // Modals
  const [isCloseModalOpen, setIsCloseModalOpen] = useState(false);
  const [closingDenoms, setClosingDenoms] = useState({ 1000: 0, 500: 0, 100: 0, 50: 0, 20: 0, 10: 0, 5: 0 });
  const [closingCashManual, setClosingCashManual] = useState('');
  const [isClosingSubmitting, setIsClosingSubmitting] = useState(false);

  // Opening form
  const [openingDenoms, setOpeningDenoms] = useState({ 1000: 0, 500: 0, 100: 0, 50: 0, 20: 0, 10: 0, 5: 0 });
  const [isOpeningSubmitting, setIsOpeningSubmitting] = useState(false);

  // Selected shift for viewing report in 'closed' or 'reports' tab
  const [viewingShift, setViewingShift] = useState(null);

  // Queries
  const { shift, summary, isLoading, mutate } = useActiveShift();
  const { shifts: closedShifts, isLoading: isLoadingClosed, mutate: mutateClosed } = useShiftsList();
  const { data: storeSettings } = useStoreSettings();

  const currency = storeSettings?.currency || "NPR";

  // Calculate opening cash total
  const calculatedOpeningCash = useMemo(() => {
    let sum = 0;
    Object.keys(openingDenoms).forEach(k => {
      sum += Number(k) * (Number(openingDenoms[k]) || 0);
    });
    return sum;
  }, [openingDenoms]);

  // Calculate closing cash total
  const calculatedClosingCash = useMemo(() => {
    if (closingCashManual !== '') return parseFloat(closingCashManual) || 0;
    let sum = 0;
    Object.keys(closingDenoms).forEach(k => {
      sum += Number(k) * (Number(closingDenoms[k]) || 0);
    });
    return sum;
  }, [closingDenoms, closingCashManual]);

  const handleDenomChange = (val, qty, isClose = false) => {
    const parsed = parseInt(qty) || 0;
    if (isClose) {
      setClosingDenoms(prev => ({ ...prev, [val]: parsed }));
      setClosingCashManual('');
    } else {
      setOpeningDenoms(prev => ({ ...prev, [val]: parsed }));
    }
  };

  const handleOpenShift = async () => {
    if (calculatedOpeningCash <= 0 && !window.confirm("Open shift with 0 opening cash?")) return;
    try {
      setIsOpeningSubmitting(true);
      await openShift(calculatedOpeningCash, openingDenoms);
      toast.success("Shift opened successfully!");
      mutate();
    } catch (err) {
      toast.error(err.message || "Failed to open shift");
    } finally {
      setIsOpeningSubmitting(false);
    }
  };

  // Calculations for active shift
  const openingCash = Number(shift?.opening_cash || 0);
  const payments = summary?.payments || [];
  const expenses = summary?.expenses || [];
  const invoices = summary?.invoices || {};
  const transactions = summary?.transactions || [];

  const cashPaymentObj = payments.find(p => (p.payment_method || '').toLowerCase() === 'cash');
  const cashSales = Number(cashPaymentObj?.total || 0);
  const otherCashIn = 0;
  const cashExpObj = expenses.find(e => (e.payment_method || '').toLowerCase() === 'cash');
  const cashExpenses = Number(cashExpObj?.total || 0);
  const cashPurchases = 0;
  const cashRefunds = 0;
  const cashWithdrawals = 0;
  const cashDeposits = 0;

  // Expected drawer cash
  const expectedDrawerCash = openingCash + cashSales + otherCashIn - cashExpenses - cashPurchases - cashRefunds - cashWithdrawals + cashDeposits;
  const actualDrawerCash = calculatedClosingCash;
  const cashDifference = actualDrawerCash - expectedDrawerCash;

  const isBalanced = Math.abs(cashDifference) < 0.01;
  const isShort = cashDifference < -0.01;
  const isOver = cashDifference > 0.01;

  const handleCloseShiftSubmit = async () => {
    try {
      setIsClosingSubmitting(true);
      await closeShift(
        shift.id,
        actualDrawerCash,
        closingDenoms,
        expectedDrawerCash,
        cashDifference
      );
      toast.success("Shift closed and drawer reconciled successfully!");
      setIsCloseModalOpen(false);
      mutate();
      mutateClosed();
    } catch (err) {
      toast.error(err.message || "Failed to close shift");
    } finally {
      setIsClosingSubmitting(false);
    }
  };

  // Filter shift transactions
  const filteredTransactions = useMemo(() => {
    if (txFilter === 'all') return transactions;
    if (txFilter === 'sales') return transactions.filter(t => t.type === 'Sale');
    if (txFilter === 'cash') return transactions.filter(t => (t.payment_method || '').toLowerCase() === 'cash');
    if (txFilter === 'qr') return transactions.filter(t => (t.payment_method || '').toLowerCase().includes('qr'));
    if (txFilter === 'card') return transactions.filter(t => (t.payment_method || '').toLowerCase().includes('card'));
    if (txFilter === 'due') return transactions.filter(t => Number(t.due_amount || 0) > 0);
    if (txFilter === 'expenses') return transactions.filter(t => t.type === 'Expense');
    if (txFilter === 'discount') return transactions.filter(t => Number(t.discount || 0) > 0);
    if (txFilter === 'complimentary') return transactions.filter(t => (t.ref_no || '').includes('COMP') || t.is_complementary);
    if (txFilter === 'refunds') return transactions.filter(t => t.type === 'Refund');
    return transactions;
  }, [transactions, txFilter]);

  const toggleSelectTx = (id) => {
    const s = new Set(selectedTx);
    if (s.has(id)) s.delete(id);
    else s.add(id);
    setSelectedTx(s);
  };

  const toggleSelectAll = () => {
    if (selectedTx.size === filteredTransactions.length) {
      setSelectedTx(new Set());
    } else {
      setSelectedTx(new Set(filteredTransactions.map(t => t.id)));
    }
  };

  return (
    <Page>
      <div className="container-xl">
        {/* Navigation Section 1 */}
        <div className="d-print-none mb-3">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
              <h2 className="page-title text-primary fw-bold mb-0">SHIFT CONTROL</h2>
              <div className="text-muted small">Physical Cashier & Drawer Session Reconciliation</div>
            </div>
            <div className="btn-group shadow-sm">
              <button 
                className={`btn btn-sm ${activeTab === 'active' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setActiveTab('active')}
              >
                Active Shifts
              </button>
              <button 
                className={`btn btn-sm ${activeTab === 'closed' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => { setActiveTab('closed'); mutateClosed(); }}
              >
                Closed Shifts
              </button>
              <button 
                className={`btn btn-sm ${activeTab === 'reports' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setActiveTab('reports')}
              >
                Shift Reports
              </button>
            </div>
          </div>
        </div>

        {/* TAB 1: ACTIVE SHIFT */}
        {activeTab === 'active' && (
          <div>
            {!shift ? (
              /* SECTION 8: SHIFT OPENING FORM */
              <div className="card shadow-sm border-0 max-w-2xl mx-auto my-4 bg-white">
                <div className="card-body p-4">
                  <div className="text-center mb-4 pb-2 border-bottom">
                    <div className="badge bg-primary-lt px-3 py-1 mb-2">Cashier Drawer Session</div>
                    <h2 className="fw-bold text-dark m-0">Open Shift Drawer</h2>
                    <p className="text-muted small mt-1">Enter opening denomination breakdown to establish physical drawer float.</p>
                  </div>

                  <h5 className="fw-bold text-uppercase text-secondary mb-3">Opening Denomination Matrix</h5>
                  <div className="table-responsive">
                    <table className="table table-vcenter table-bordered">
                      <thead className="table-light">
                        <tr>
                          <th>Denomination ({currency})</th>
                          <th className="text-center" style={{ width: '130px' }}>Quantity</th>
                          <th className="text-end">Amount ({currency})</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[1000, 500, 100, 50, 20, 10, 5].map(val => (
                          <tr key={val}>
                            <td className="fw-bold">{val}</td>
                            <td>
                              <input 
                                type="number" 
                                min="0"
                                className="form-control form-control-sm text-center fw-bold"
                                value={openingDenoms[val] || ''}
                                placeholder="0"
                                onChange={(e) => handleDenomChange(val, e.target.value)}
                              />
                            </td>
                            <td className="text-end fw-bold">
                              {((openingDenoms[val] || 0) * val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                          </tr>
                        ))}
                        <tr className="table-primary border-top border-dark">
                          <td colSpan={2} className="fw-bold fs-3 text-primary text-uppercase">Total Opening Cash</td>
                          <td className="text-end fw-bold fs-3 text-primary">
                            {currency} {calculatedOpeningCash.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="mt-4">
                    <button 
                      className="btn btn-primary btn-lg w-100 shadow-sm"
                      disabled={isOpeningSubmitting}
                      onClick={handleOpenShift}
                    >
                      {isOpeningSubmitting ? 'Opening Shift...' : 'CONFIRM & OPEN SHIFT'}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              /* SECTION 7, 9, 10: ACTIVE SHIFT DASHBOARD */
              <div className="card shadow-sm border-0 mb-4 bg-white p-4">
                {/* Active Shift Header */}
                <div className="d-flex justify-content-between align-items-start mb-4 border-bottom pb-3 flex-wrap gap-2">
                  <div>
                    <div className="d-flex align-items-center gap-2">
                      <h2 className="fw-bold text-success m-0">● SHIFT #{shift.id}</h2>
                      <span className="badge bg-success text-white px-2 py-1 text-uppercase">ACTIVE</span>
                    </div>
                    <div className="text-muted mt-1">
                      Opened: <strong className="text-dark">{new Date(shift.opened_at).toLocaleString()}</strong>
                    </div>
                    <div className="text-muted">
                      Cashier: <strong className="text-dark">{shift.cashier_name || 'Current User'}</strong>
                    </div>
                  </div>
                  <div className="d-flex gap-2 flex-wrap">
                    <button 
                      className="btn btn-outline-primary"
                      onClick={() => window.print()}
                    >
                      <IconPrinter size={16} className="me-1" /> Print Shift
                    </button>
                    <button 
                      className="btn btn-danger"
                      onClick={() => setIsCloseModalOpen(true)}
                    >
                      Close Shift
                    </button>
                  </div>
                </div>

                {/* SECTION 9: SHIFT COLLECTION & SUMMARY */}
                <div className="row g-4 mb-4">
                  <div className="col-lg-6">
                    <div className="border rounded-3 p-3 bg-light-lt h-100">
                      <h4 className="fw-bold text-uppercase text-secondary border-bottom pb-2 mb-3">Payment Collection</h4>
                      <div className="table-responsive">
                        <table className="table table-vcenter table-bordered bg-white shadow-xs">
                          <thead className="table-light">
                            <tr>
                              <th>Payment Type</th>
                              <th className="text-end">Amount ({currency})</th>
                              <th className="text-center">Transactions</th>
                              <th className="text-center" style={{ width: '60px' }}>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {payments.map((p, idx) => (
                              <tr key={idx}>
                                <td className="fw-medium">{p.payment_method}</td>
                                <td className="text-end fw-bold">{Number(p.total || 0).toFixed(2)}</td>
                                <td className="text-center">{p.tx_count || 1}</td>
                                <td className="text-center">
                                  <button className="btn btn-sm btn-icon btn-ghost-primary" onClick={() => setTxFilter(p.payment_method.toLowerCase())}>
                                    <IconEye size={16} />
                                  </button>
                                </td>
                              </tr>
                            ))}
                            {payments.length === 0 && (
                              <tr>
                                <td colSpan={4} className="text-center text-muted py-2">No collections recorded yet</td>
                              </tr>
                            )}
                            <tr className="table-primary border-top border-dark">
                              <td className="fw-bold">Total Collected</td>
                              <td className="text-end fw-bold">
                                {currency} {payments.reduce((acc, p) => acc + Number(p.total || 0), 0).toFixed(2)}
                              </td>
                              <td colSpan={2}></td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      {/* Supplementary details */}
                      <div className="row g-2 mt-2 small text-muted">
                        <div className="col-6">Sales Due: <strong className="text-warning">{currency} {Number(invoices.due_sales || 0).toFixed(2)}</strong></div>
                        <div className="col-6">Discount: <strong className="text-danger">-{currency} {Number(invoices.total_discount || 0).toFixed(2)}</strong></div>
                        <div className="col-6">Complementary: <strong className="text-purple">{currency} {Number(invoices.total_complementary || 0).toFixed(2)}</strong></div>
                        <div className="col-6">VAT / Tax: <strong className="text-dark">{currency} {Number(invoices.total_tax || 0).toFixed(2)}</strong></div>
                      </div>
                    </div>
                  </div>

                  {/* SECTION 10: SHIFT CASH RECONCILIATION */}
                  <div className="col-lg-6">
                    <div className="border rounded-3 p-3 bg-light-lt h-100">
                      <h4 className="fw-bold text-uppercase text-secondary border-bottom pb-2 mb-3">Cash Drawer Reconciliation</h4>
                      <div className="table-responsive">
                        <table className="table table-vcenter table-bordered bg-white shadow-xs">
                          <tbody>
                            <tr className="table-light">
                              <td>Opening Cash (Drawer Float)</td>
                              <td className="text-end fw-bold text-primary">+{currency} {openingCash.toFixed(2)}</td>
                            </tr>
                            <tr>
                              <td>+ Cash Sales</td>
                              <td className="text-end fw-bold text-success">+{currency} {cashSales.toFixed(2)}</td>
                            </tr>
                            <tr>
                              <td>+ Other Cash In</td>
                              <td className="text-end fw-medium text-success">+{currency} {otherCashIn.toFixed(2)}</td>
                            </tr>
                            <tr>
                              <td>- Cash Expenses</td>
                              <td className="text-end fw-medium text-danger">-{currency} {cashExpenses.toFixed(2)}</td>
                            </tr>
                            <tr>
                              <td>- Cash Purchases</td>
                              <td className="text-end fw-medium text-danger">-{currency} {cashPurchases.toFixed(2)}</td>
                            </tr>
                            <tr>
                              <td>- Cash Refunds</td>
                              <td className="text-end fw-medium text-danger">-{currency} {cashRefunds.toFixed(2)}</td>
                            </tr>
                            <tr>
                              <td>- Cash Withdrawals</td>
                              <td className="text-end fw-medium text-danger">-{currency} {cashWithdrawals.toFixed(2)}</td>
                            </tr>
                            <tr>
                              <td>+ Cash Deposits</td>
                              <td className="text-end fw-medium text-success">+{currency} {cashDeposits.toFixed(2)}</td>
                            </tr>
                            <tr className="table-dark">
                              <td className="fw-bold fs-4">Expected Drawer Cash</td>
                              <td className="text-end fw-bold fs-4 text-warning">
                                {currency} {expectedDrawerCash.toFixed(2)}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <div className="small text-muted mt-2">
                        * Only physical drawer transactions are included. Digital wallets, QR, card, and receivables are excluded.
                      </div>
                    </div>
                  </div>
                </div>

                {/* SECTION 11: SHIFT TRANSACTIONS */}
                <div className="border rounded-3 p-3 bg-white mt-4">
                  <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                    <div>
                      <h4 className="fw-bold text-uppercase text-secondary m-0">Shift Transactions</h4>
                      <div className="small text-muted">Audited transaction log for Shift #{shift.id}</div>
                    </div>
                    {/* Filter Tabs */}
                    <div className="btn-group btn-group-sm flex-wrap">
                      {['all', 'sales', 'cash', 'qr', 'card', 'due', 'expenses', 'discount', 'complimentary', 'refunds'].map(tab => (
                        <button 
                          key={tab}
                          className={`btn ${txFilter === tab ? 'btn-primary' : 'btn-outline-secondary'} text-capitalize`}
                          onClick={() => setTxFilter(tab)}
                        >
                          {tab}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Print Actions Bar Section 12 */}
                  <div className="d-flex gap-2 mb-3 d-print-none align-items-center">
                    <span className="small text-muted me-2">Print Options:</span>
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => window.print()}>Print Shift Summary</button>
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => window.print()}>Print Detailed Shift Report</button>
                    <button 
                      className="btn btn-sm btn-outline-secondary" 
                      disabled={selectedTx.size === 0}
                      onClick={() => {
                        toast.success(`Printing ${selectedTx.size} selected transactions`);
                        window.print();
                      }}
                    >
                      Print Selected ({selectedTx.size})
                    </button>
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => window.print()}>Print All Transactions</button>
                  </div>

                  <div className="table-responsive">
                    <table className="table table-vcenter table-hover table-bordered table-striped">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: '40px' }} className="text-center">
                            <input 
                              type="checkbox" 
                              className="form-check-input"
                              checked={selectedTx.size === filteredTransactions.length && filteredTransactions.length > 0}
                              onChange={toggleSelectAll}
                            />
                          </th>
                          <th>Time</th>
                          <th>Invoice / Ref</th>
                          <th>Type</th>
                          <th>Payment</th>
                          <th className="text-end">Amount ({currency})</th>
                          <th className="text-center" style={{ width: '90px' }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredTransactions.map(tx => (
                          <tr key={tx.id}>
                            <td className="text-center">
                              <input 
                                type="checkbox" 
                                className="form-check-input"
                                checked={selectedTx.has(tx.id)}
                                onChange={() => toggleSelectTx(tx.id)}
                              />
                            </td>
                            <td className="small text-muted">{new Date(tx.time).toLocaleTimeString()}</td>
                            <td className="fw-bold">{tx.ref_no || `INV-${tx.id}`}</td>
                            <td><span className={`badge ${tx.type === 'Sale' ? 'bg-blue-lt' : 'bg-red-lt'}`}>{tx.type}</span></td>
                            <td><span className="badge bg-secondary-lt">{tx.payment_method}</span></td>
                            <td className="text-end fw-bold">{Number(tx.amount || 0).toFixed(2)}</td>
                            <td className="text-center">
                              <button className="btn btn-sm btn-icon btn-ghost-primary" title="View"><IconEye size={16} /></button>
                              <button className="btn btn-sm btn-icon btn-ghost-secondary ms-1" title="Print" onClick={() => window.print()}><IconPrinter size={16} /></button>
                            </td>
                          </tr>
                        ))}
                        {filteredTransactions.length === 0 && (
                          <tr>
                            <td colSpan={7} className="text-center text-muted py-4">No transactions matching this filter.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* TAB 2: CLOSED SHIFTS */}
        {activeTab === 'closed' && (
          <div className="card shadow-sm border-0 bg-white p-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h3 className="fw-bold m-0">Closed Shifts History</h3>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => mutateClosed()}>
                <IconRefresh size={16} className="me-1" /> Refresh
              </button>
            </div>

            <div className="table-responsive">
              <table className="table table-vcenter table-hover table-bordered table-striped">
                <thead className="table-light">
                  <tr>
                    <th>Shift #</th>
                    <th>Cashier</th>
                    <th>Opened At</th>
                    <th>Closed At</th>
                    <th className="text-end">Opening Cash</th>
                    <th className="text-end">Closing Cash</th>
                    <th className="text-end">Expected Cash</th>
                    <th className="text-end">Difference</th>
                    <th className="text-center">Status</th>
                    <th className="text-center" style={{ width: '90px' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {(closedShifts || []).map(s => {
                    const diff = Number(s.cash_difference || 0);
                    const isBal = Math.abs(diff) < 0.01;
                    return (
                      <tr key={s.id}>
                        <td className="fw-bold">#{s.id}</td>
                        <td>{s.cashier_name || 'Staff'}</td>
                        <td className="small text-muted">{new Date(s.opened_at).toLocaleString()}</td>
                        <td className="small text-muted">{s.closed_at ? new Date(s.closed_at).toLocaleString() : 'In Progress'}</td>
                        <td className="text-end">{currency} {Number(s.opening_cash || 0).toFixed(2)}</td>
                        <td className="text-end">{currency} {Number(s.closing_cash || 0).toFixed(2)}</td>
                        <td className="text-end">{currency} {Number(s.expected_cash || 0).toFixed(2)}</td>
                        <td className={`text-end fw-bold ${diff < 0 ? 'text-danger' : diff > 0 ? 'text-success' : 'text-muted'}`}>
                          {currency} {diff.toFixed(2)}
                        </td>
                        <td className="text-center">
                          <span className={`badge ${isBal ? 'bg-success' : diff < 0 ? 'bg-danger' : 'bg-warning'} text-white`}>
                            {isBal ? '✓ BALANCED' : diff < 0 ? 'SHORT' : 'OVER'}
                          </span>
                        </td>
                        <td className="text-center">
                          <button 
                            className="btn btn-sm btn-icon btn-ghost-primary" 
                            title="View Shift Report"
                            onClick={() => { setViewingShift(s); setActiveTab('reports'); }}
                          >
                            <IconEye size={17} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {(!closedShifts || closedShifts.length === 0) && (
                    <tr>
                      <td colSpan={10} className="text-center text-muted py-4">No closed shifts recorded yet.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* TAB 3: SHIFT REPORTS */}
        {activeTab === 'reports' && (
          <div className="card shadow-sm border-0 bg-white p-4">
            <h3 className="fw-bold mb-3">Shift Reports & Audit Analysis</h3>
            <p className="text-muted">Select any shift from the Closed Shifts tab to inspect its detailed denomination breakdown, cash reconciliation audit, and print statements.</p>
            {viewingShift ? (
              <div className="border rounded-3 p-3 bg-light-lt mt-3">
                <h4>Inspecting Shift #{viewingShift.id} ({viewingShift.cashier_name})</h4>
                <div className="row g-3">
                  <div className="col-md-4">Opened: <strong>{new Date(viewingShift.opened_at).toLocaleString()}</strong></div>
                  <div className="col-md-4">Closed: <strong>{viewingShift.closed_at ? new Date(viewingShift.closed_at).toLocaleString() : 'Active'}</strong></div>
                  <div className="col-md-4">Difference: <strong>{currency} {Number(viewingShift.cash_difference || 0).toFixed(2)}</strong></div>
                </div>
                <div className="mt-3">
                  <button className="btn btn-sm btn-primary" onClick={() => window.print()}>Print This Shift Report</button>
                </div>
              </div>
            ) : (
              <div className="alert alert-info">No shift selected. Click the 👁 icon on any shift in "Closed Shifts" to view its full report.</div>
            )}
          </div>
        )}

        {/* SECTION 10: CLOSING SHIFT MODAL WITH DENOMINATION */}
        {isCloseModalOpen && (
          <div className="modal modal-blur fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
            <div className="modal-dialog modal-lg modal-dialog-centered">
              <div className="modal-content shadow-xl border-0">
                <div className="modal-header bg-dark text-white">
                  <div>
                    <h4 className="modal-title fw-bold">Close Shift #{shift.id} & Drawer Reconciliation</h4>
                    <div className="small text-white-50">Count physical cash in drawer to reconcile with expected balance</div>
                  </div>
                  <button type="button" className="btn-close btn-close-white" onClick={() => setIsCloseModalOpen(false)}></button>
                </div>
                <div className="modal-body p-4">
                  <div className="row g-4">
                    {/* Denomination entry */}
                    <div className="col-md-6 border-end">
                      <h5 className="fw-bold text-uppercase text-secondary mb-3">Closing Denominations</h5>
                      <div className="table-responsive">
                        <table className="table table-vcenter table-sm table-bordered">
                          <thead className="table-light">
                            <tr>
                              <th>Note</th>
                              <th className="text-center" style={{ width: '100px' }}>Qty</th>
                              <th className="text-end">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {[1000, 500, 100, 50, 20, 10, 5].map(val => (
                              <tr key={val}>
                                <td className="fw-bold">{val}</td>
                                <td>
                                  <input 
                                    type="number"
                                    min="0"
                                    className="form-control form-control-sm text-center"
                                    value={closingDenoms[val] || ''}
                                    placeholder="0"
                                    onChange={(e) => handleDenomChange(val, e.target.value, true)}
                                  />
                                </td>
                                <td className="text-end fw-bold">
                                  {((closingDenoms[val] || 0) * val).toFixed(2)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      <div className="mt-2">
                        <label className="form-label small text-muted">Or enter total actual cash manually:</label>
                        <input 
                          type="number" 
                          step="any"
                          className="form-control form-control-sm"
                          placeholder="Manual total actual cash"
                          value={closingCashManual}
                          onChange={(e) => setClosingCashManual(e.target.value)}
                        />
                      </div>
                    </div>

                    {/* Reconciliation summary */}
                    <div className="col-md-6 d-flex flex-column justify-content-between">
                      <div>
                        <h5 className="fw-bold text-uppercase text-secondary mb-3">Reconciliation Status</h5>
                        
                        <div className="card bg-light border-0 p-3 mb-3">
                          <div className="d-flex justify-content-between mb-2">
                            <span>Expected Drawer Cash:</span>
                            <span className="fw-bold">{currency} {expectedDrawerCash.toFixed(2)}</span>
                          </div>
                          <div className="d-flex justify-content-between mb-2">
                            <span>Actual Drawer Cash:</span>
                            <span className="fw-bold text-primary">{currency} {actualDrawerCash.toFixed(2)}</span>
                          </div>
                          <hr className="my-2" />
                          <div className="d-flex justify-content-between fs-3 fw-bold">
                            <span>Difference:</span>
                            <span className={cashDifference < 0 ? 'text-danger' : cashDifference > 0 ? 'text-success' : 'text-dark'}>
                              {currency} {cashDifference.toFixed(2)}
                            </span>
                          </div>
                        </div>

                        {/* Status banner */}
                        <div className={`alert ${isBalanced ? 'alert-success' : isShort ? 'alert-danger' : 'alert-warning'} text-center py-2 fw-bold`}>
                          {isBalanced ? '✓ BALANCED' : isShort ? `⚠ CASH SHORTAGE: ${currency} ${Math.abs(cashDifference).toFixed(2)}` : `⚠ CASH OVERAGE: ${currency} ${cashDifference.toFixed(2)}`}
                        </div>
                      </div>

                      <div className="d-flex gap-2 mt-3">
                        <button type="button" className="btn btn-secondary w-50" onClick={() => setIsCloseModalOpen(false)}>
                          Cancel
                        </button>
                        <button 
                          type="button" 
                          className="btn btn-danger w-50"
                          disabled={isClosingSubmitting}
                          onClick={handleCloseShiftSubmit}
                        >
                          {isClosingSubmitting ? 'Closing...' : 'Confirm & Close'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </Page>
  );
}
